import * as React from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  FilterSearch,
  FilterSelect,
  FiltersToolbar,
  formatDateTimeVn,
} from "@chotca/ui";
import { useOrgActivity } from "@chotca/api-client";
import { useAuthStore } from "@chotca/auth";
import { formatActivityAction } from "@chotca/config";
import type { ActivityLog, ActorRole } from "@chotca/config";
import { useTableFilters } from "@/lib/use-table-filters";
import { DataTable, usePagination, type Column } from "@/components/data-table";

const ROLE_LABEL: Record<ActorRole, string> = {
  WORKER: "Ứng viên",
  EMPLOYER: "Nhân viên",
  ADMIN: "Admin",
  SYSTEM: "Hệ thống",
};

const META_KEY_LABELS: Record<string, string> = {
  title: "Tiêu đề",
  name: "Tên",
  state: "Trạng thái",
  pack: "Gói",
  credits: "Credits",
  amountVnd: "Số tiền",
  amount: "Số tiền",
  price: "Giá",
  reason: "Lý do",
  status: "Trạng thái",
  email: "Email",
  phone: "SĐT",
  role: "Vai trò",
  postingId: "Tin",
  orgId: "Doanh nghiệp",
  userId: "Người dùng",
  count: "Số lượng",
};

const VND_KEYS = new Set(["amountVnd", "amount", "price", "totalVnd"]);

// Maps an activity target (and id-bearing metadata keys) to an in-app detail
// route, so the short id renders as a clickable link. Anything not listed here
// has no employer-facing detail page and stays plain text.
const TARGET_TYPE_HREF: Record<string, (id: string) => string> = {
  Posting: (id) => `/postings/${id}`,
  Application: (id) => `/applicants?app=${id}`,
};
const META_ID_HREF: Record<string, (id: string) => string> = {
  postingId: (id) => `/postings/${id}`,
  applicationId: (id) => `/applicants?app=${id}`,
};

// Short, clickable id chip with a dotted underline; falls back to muted plain
// text when there's no detail route for that entity.
function IdRef({ id, href }: { id: string; href: string | null }) {
  const short = id.slice(0, 8);
  if (!href) return <span className="font-mono text-ash">{short}</span>;
  return (
    <Link
      to={href}
      title={id}
      className="font-mono text-ink underline decoration-dotted underline-offset-2 hover:text-lime"
    >
      {short}
    </Link>
  );
}

function formatMetaValue(key: string, value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "boolean") return value ? "Có" : "Không";
  if (typeof value === "number") {
    if (VND_KEYS.has(key))
      return new Intl.NumberFormat("vi-VN").format(value) + "₫";
    return new Intl.NumberFormat("vi-VN").format(value);
  }
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function MetadataCell({
  metadata,
}: {
  metadata?: Record<string, unknown> | null;
}) {
  const [open, setOpen] = React.useState(false);
  if (!metadata || typeof metadata !== "object") {
    return <span className="text-xs text-ash">—</span>;
  }
  const entries = Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== "",
  );
  if (entries.length === 0) {
    return <span className="text-xs text-ash">—</span>;
  }
  return (
    <div className="max-w-80">
      <div className="flex flex-wrap gap-x-2 gap-y-1">
        {entries.map(([k, v]) => {
          const label = META_KEY_LABELS[k] ?? k;
          const hrefFn = META_ID_HREF[k];
          if (hrefFn && typeof v === "string" && v) {
            return (
              <span key={k} className="text-xs text-ink">
                <span className="text-ash">{label}:</span>{" "}
                <IdRef id={v} href={hrefFn(v)} />
              </span>
            );
          }
          const display = formatMetaValue(k, v);
          return (
            <span key={k} className="text-xs text-ink">
              <span className="text-ash">{label}:</span>{" "}
              <span
                className={
                  k === "title" || k === "name"
                    ? "font-medium"
                    : VND_KEYS.has(k)
                      ? "font-mono tabular-nums"
                      : ""
                }
                title={display}
              >
                {display.length > 60 ? display.slice(0, 60) + "…" : display}
              </span>
            </span>
          );
        })}
      </div>
      {entries.length > 3 || JSON.stringify(metadata).length > 80 ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-1 text-[10px] uppercase tracking-wide text-ash hover:text-ink"
        >
          {open ? "Ẩn JSON" : "Xem JSON"}
        </button>
      ) : null}
      {open ? (
        <pre className="mt-1 max-h-40 overflow-auto rounded bg-mist p-2 text-[11px] leading-tight text-ink">
          {JSON.stringify(metadata, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

export function OrgActivityClient() {
  const t = useTranslations("orgSettings");
  const orgId = useAuthStore((s) => s.activeOrgId);
  const { page, pageSize, setPage, setPageSize } = usePagination();
  const { data, isLoading, isFetching } = useOrgActivity(orgId, {
    page,
    limit: pageSize,
  });
  const allRows = (data?.items ?? []) as ActivityLog[];

  const filters = useTableFilters({
    search: {},
    actorRole: {},
    targetType: {},
  });
  const search = filters.values.search ?? "";
  const actorRole = filters.applied.actorRole ?? "";
  const targetType = filters.applied.targetType ?? "";
  React.useEffect(() => {
    setPage(1);
  }, [search, actorRole, targetType, setPage]);

  const rows = allRows.filter((r) => {
    if (actorRole && r.actorRole !== actorRole) return false;
    if (targetType && r.targetType !== targetType) return false;
    if (search) {
      const hay = (
        r.action +
        (r.targetType ?? "") +
        (r.actorPhone ?? "") +
        (r.actorUserId ?? "")
      ).toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const targetTypeOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of allRows) if (r.targetType) set.add(r.targetType);
    return Array.from(set)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [allRows]);

  const columns: Column<ActivityLog>[] = [
    {
      key: "time",
      header: "Thời gian",
      cell: (r) => (
        <span className="whitespace-nowrap text-xs">
          {formatDateTimeVn(new Date(r.createdAt))}
        </span>
      ),
    },
    {
      key: "action",
      header: "Hành động",
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">
            {formatActivityAction(r.action)}
          </p>
          <p className="truncate font-mono text-[10px] text-ink/40">
            {r.action}
          </p>
        </div>
      ),
    },
    {
      key: "entity",
      header: "Đối tượng",
      cell: (r) => (
        <span className="text-xs">
          {r.targetType ?? "—"}
          {r.targetId ? (
            <span className="ml-1">
              <IdRef
                id={r.targetId}
                href={
                  r.targetType
                    ? (TARGET_TYPE_HREF[r.targetType]?.(r.targetId) ?? null)
                    : null
                }
              />
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "meta",
      header: "Ghi chú",
      cell: (r) => <MetadataCell metadata={r.metadata} />,
    },
  ];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-display-lg font-extrabold text-ink">
          {t("activityTitle")}
        </h1>
      </header>
      <nav className="flex flex-wrap gap-2">
        <Link
          to="/org"
          className="rounded-full border border-smoke px-4 py-1.5 text-sm font-semibold text-ink hover:bg-mist"
        >
          {t("tabInfo")}
        </Link>
        <span className="rounded-full bg-ink px-4 py-1.5 text-sm font-semibold text-paper">
          {t("tabActivity")}
        </span>
      </nav>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
      >
        <FilterSearch
          value={search}
          onChange={(v) => filters.set("search", v)}
          placeholder="Tìm hành động / SĐT / đối tượng..."
        />
        <FilterSelect
          value={filters.values.actorRole ?? ""}
          onValueChange={(v) => filters.set("actorRole", v)}
          placeholder="Mọi vai trò"
          options={[
            { value: "EMPLOYER", label: ROLE_LABEL.EMPLOYER },
            { value: "WORKER", label: ROLE_LABEL.WORKER },
            { value: "ADMIN", label: ROLE_LABEL.ADMIN },
            { value: "SYSTEM", label: ROLE_LABEL.SYSTEM },
          ]}
        />
        <FilterSelect
          value={filters.values.targetType ?? ""}
          onValueChange={(v) => filters.set("targetType", v)}
          placeholder="Mọi đối tượng"
          options={targetTypeOptions}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(r) => r.id}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
}
